import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { formatDecimal, numberLocale } from '@/lib/language';
import { LINKED_ADS_ACCOUNT_QUERY_KEY } from './useLinkedAdsAccount';

/**
 * Lesezugriff auf den Google-Ads-Pfad (Spec AP4).
 */

// ── Spalten von google_ads_accounts ─────────────────────────────────────────
/**
 * ⚠ DIESE LISTE IST TRAGEND, NICHT KOSMETIK. NICHT durch `*` ersetzen.
 *
 * `google_ads_accounts` hat ein SPALTENWEISES SELECT-Grant fuer
 * `authenticated` (Migration `20260802120000_google_ads_read_path.sql`,
 * Abschnitt 4). Die vier Credential-Spalten (`access_token`, `refresh_token`,
 * `token_expires_at`, `tokens_encrypted`) sind bewusst NICHT lesbar: das
 * Refresh-Token traegt wegen `include_granted_scopes=true` die Vereinigung aus
 * content- UND adwords-Scope, und es gibt keinen Grund, ein langlebiges
 * Credential ueber die Data API in einen Browser zu schicken.
 *
 * Folge: ein `select('*')` gegen diese Tabelle scheitert mit PostgREST-Fehler
 * `42501` und die ganze Seite bleibt leer. Die naheliegende "Vereinfachung"
 * bricht also genau das, was sie aufraeumen soll.
 */
export const ADS_ACCOUNT_COLUMNS =
  'id, google_account_id, ads_customer_id, descriptive_name, currency, time_zone, ' +
  'is_manager, is_active, linked_merchant_center_id, connected_at, last_pull_at, ' +
  // login_customer_id: MCC-Support (#181). Das Spalten-Grant wurde in
  // Migration 20260816130000 explizit um diese Spalte erweitert -- ohne den
  // GRANT liefe genau dieses SELECT in einen 42501.
  'last_pull_error, login_customer_id, created_at, updated_at';

export interface AdsAccount {
  id: string;
  google_account_id: string;
  ads_customer_id: string;
  descriptive_name: string | null;
  currency: string | null;
  time_zone: string | null;
  is_manager: boolean;
  is_active: boolean;
  linked_merchant_center_id: string | null;
  /**
   * MCC-Kundennummer, ueber die dieses Konto erreichbar ist (#181). `null` =
   * Direktzugriff. Das Panel gruppiert danach: verwaltete Konten stehen
   * eingerueckt unter der Zeile des Verwaltungskontos.
   */
  login_customer_id: string | null;
  connected_at: string;
  last_pull_at: string | null;
  last_pull_error: string | null;
  created_at: string;
  updated_at: string;
}

// ── Zahlen vom Draht ────────────────────────────────────────────────────────
/**
 * EINE Stelle, an der aus einem Wire-Wert eine JS-Zahl wird.
 *
 * Warum ueberhaupt eine Funktion und nicht `+row.cost_micros`: die drei
 * Ganzzahl-Spalten sind `bigint`, und `conversions`/`conversions_value` sind
 * `numeric`. Beide Typen koennen ueber PostgREST als **String** ankommen --
 * proto3 kodiert `int64` im JSON grundsaetzlich als String, und die Edge
 * Function reicht die Werte genau so durch (Praezedenz
 * `pull-price-benchmarks/index.ts:191-195`, Postgres castet beim Schreiben).
 * Ein `+`-Praefix waere fuer realistische Werte richtig und faellt trotzdem
 * still auf `NaN`, sobald ein Wert `null` ist oder ein Format wechselt.
 *
 * Praezision: `cost_micros` ist erst ab ~9.007.199.254 Waehrungseinheiten
 * (2^53 Micros) nicht mehr exakt in einer JS-Zahl darstellbar. Fuer Werbekosten
 * eines Produktfeeds ist das keine erreichbare Groesse; sollte es je eine
 * werden, ist die Umrechnung hier die einzige Stelle, die sich aendert.
 */
export function toWireNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/** Micros in Kontowaehrung. `cost_micros` wird UNVERAENDERT gespeichert (Spec D6). */
export function microsToUnits(costMicros: unknown): number {
  return toWireNumber(costMicros) / 1e6;
}

/**
 * ROAS = Conversion-Wert / Kosten, clientseitig gerechnet (Spec AP4).
 *
 * `conversions_value` steht in KONTOWAEHRUNG (proto DOUBLE, ausdruecklich
 * keine Micros), `cost_micros` in Micros -- die Division muss deshalb erst
 * durch 1e6 und darf nicht auf zwei Micros-Werte losgelassen werden.
 *
 * Kosten null ergibt KEINEN ROAS. Weder `Infinity` (was die Anzeige zerlegt)
 * noch `0` (was eine gemessene Null behaupten wuerde, obwohl gar nichts
 * ausgegeben wurde): ein Produkt ohne Kosten hat schlicht kein Verhaeltnis,
 * und das ist der Null-Platzhalter.
 */
export function computeRoas(conversionsValue: unknown, costMicros: unknown): number | null {
  const cost = microsToUnits(costMicros);
  if (!(cost > 0)) return null;
  const roas = toWireNumber(conversionsValue) / cost;
  return Number.isFinite(roas) ? roas : null;
}

/** ISO-4217 ist dreistellig und alphabetisch. Alles andere wirft in `Intl`. */
function normalizeCurrency(currency: string | null | undefined): string | null {
  if (typeof currency !== 'string') return null;
  const code = currency.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

/**
 * Geldbetrag in der Waehrung DES KONTOS, nicht in einer angenommenen.
 *
 * `formatCurrency` in `usePerformance.ts` haengt ein festes Euro-Zeichen an.
 * Das geht hier nicht: die Kontowaehrung steht in `google_ads_accounts.currency`
 * (aus `customer.currency_code`), und CHF- neben EUR-Konten sind in diesem
 * Produkt der Normalfall. Ein falsches Waehrungszeichen ist schlimmer als gar
 * keines, deshalb faellt der unbekannte Fall auf die nackte Zahl zurueck statt
 * auf ein geratenes Symbol.
 */
export function formatMoney(
  value: number | null | undefined,
  currency: string | null | undefined,
  language: string | null | undefined,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return formatDecimal(null, language, 2);
  }
  const code = normalizeCurrency(currency);
  if (!code) return formatDecimal(value, language, 2);
  try {
    return new Intl.NumberFormat(numberLocale(language), {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Ein Code, den diese Laufzeit nicht kennt, darf die Tabelle nicht
    // umwerfen. Zahl plus Code ist immer noch eine wahre Aussage.
    return `${formatDecimal(value, language, 2)} ${code}`;
  }
}

// ── Zeilen aus der RPC ads_performance_summary ──────────────────────────────

/** Zeile der RPC ads_performance_summary. numeric/bigint koennen als String ankommen (PostgREST). */
export interface AdsSummaryWireRow {
  product_id: string;
  title: string | null;
  day_rows: number | string | null;
  latest_date: string | null;
  impressions: number | string | null;
  clicks: number | string | null;
  cost_micros: number | string | null;
  conversions: number | string | null;
  conversions_value: number | string | null;
  /** Anzahl ALLER Produktgruppen vor dem LIMIT (`COUNT(*) OVER ()`), auf jeder
   *  Zeile identisch. Neu in RPC v2 (Migration 20260817100000); `null` deckt
   *  eine v1-Antwort ohne die Spalte ab. */
  total_products: number | string | null;
}

export interface AdsProductRow {
  product_id: string;
  title: string | null;
  impressions: number;
  clicks: number;
  /** Rohwert, so wie er in der Tabelle steht. */
  costMicros: number;
  /** Kosten in Kontowaehrung, also `costMicros / 1e6`. */
  cost: number;
  conversions: number;
  conversionsValue: number;
  /** `null` heisst "kein Verhaeltnis", nicht "null". Siehe `computeRoas`. */
  roas: number | null;
}

// ── Hook: verbundene Ads-Konten ─────────────────────────────────────────────

/**
 * Alle Ads-Konten des angemeldeten Nutzers. RLS scoped ueber
 * `google_ads_accounts -> google_accounts.user_id = auth.uid()`.
 *
 * ⚠ WAS DIESER HOOK NICHT WISSEN KANN: ob ein Konto noch Credentials traegt.
 * `disconnect_google_ads_account` nullt `access_token`/`refresh_token` und
 * laesst die Zeile stehen (mit Absicht -- die gesammelte Leistungshistorie
 * gehoert dem Kunden), und die Token-Spalten sind fuer `authenticated` nicht
 * lesbar. "Verbunden" ist deshalb clientseitig nur als "es gibt eine Zeile"
 * beobachtbar. Praktische Folge fuer die Seite: der Verbinden-Knopf bleibt
 * auch dann erreichbar, wenn schon Zeilen existieren -- er ist zugleich der
 * Wiederverbinden-Weg.
 */
export function useAdsAccounts() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['ads-accounts', user?.id ?? null],
    enabled: !!user?.id,
    staleTime: 120_000,
    queryFn: async (): Promise<AdsAccount[]> => {
      const { data, error } = await supabase
        .from('google_ads_accounts')
        .select(ADS_ACCOUNT_COLUMNS)
        .order('connected_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as AdsAccount[];
    },
  });
}

// ── Hook: Leistung je Produkt ───────────────────────────────────────────────

export const ADS_WINDOW_OPTIONS = [7, 14, 30] as const;

export interface AdsPerformanceResult {
  products: AdsProductRow[];
  /** Juengstes Datum mit Daten im Fenster, oder `null`. */
  latestDate: string | null;
  /** Rohe Tageszeilen HINTER der Verdichtung (Summe der `day_rows` aus der
   *  RPC), fuer die Unterscheidung "nichts geladen" von "geladen, aber alles
   *  auf null". */
  rowCount: number;
  /** Gesamtzahl der Produkte mit Ads-Daten im Fenster. Kann GROESSER sein als
   *  `products.length`: die RPC kappt auf die teuersten 1000 (PostgREST
   *  db-max-rows), `total_products` zaehlt VOR dem LIMIT. 0 bei leerem
   *  Ergebnis. Das Panel muss diese Zahl nennen, nicht `products.length` --
   *  sonst behauptet es "1.000 Produkte" ueber einen 11.778-Produkte-Feed. */
  totalProducts: number;
}

/**
 * Summenzeilen der RPC in das Ergebnis-Format des Panels uebersetzen.
 *
 * Reines Mapping, KEINE Aggregation mehr: die lief frueher hier im Browser
 * ueber alle Roh-Tageszeilen und ist mit Issue #180 in die RPC gewandert
 * (Begruendung am Hook unten). ROAS bleibt bewusst clientseitig gerechnet --
 * eine Formel, eine Stelle (`computeRoas`), und `null` heisst weiterhin
 * "kein Verhaeltnis", nicht "null".
 */
export function mapSummaryRows(rows: readonly AdsSummaryWireRow[]): AdsPerformanceResult {
  const products: AdsProductRow[] = rows.map((row) => {
    const costMicros = toWireNumber(row.cost_micros);
    return {
      product_id: row.product_id,
      title: row.title ?? null,
      impressions: toWireNumber(row.impressions),
      clicks: toWireNumber(row.clicks),
      costMicros,
      cost: costMicros / 1e6,
      conversions: toWireNumber(row.conversions),
      conversionsValue: toWireNumber(row.conversions_value),
      roas: computeRoas(row.conversions_value, row.cost_micros),
    };
  });
  // Die RPC sortiert bereits; hier nochmal als Defense, damit die Reihenfolge
  // nicht von der Serialisierung abhaengt (billig auf <= Produktzahl Zeilen).
  products.sort((a, b) => {
    if (b.costMicros !== a.costMicros) return b.costMicros - a.costMicros;
    if (b.clicks !== a.clicks) return b.clicks - a.clicks;
    return a.product_id.localeCompare(b.product_id);
  });
  let latestDate: string | null = null;
  let rowCount = 0;
  for (const row of rows) {
    if (typeof row.latest_date === 'string' && (latestDate === null || row.latest_date > latestDate)) {
      latestDate = row.latest_date;
    }
    rowCount += toWireNumber(row.day_rows);
  }
  // Die ehrliche Gesamtzahl steht auf jeder Zeile identisch (COUNT OVER vor
  // dem LIMIT der RPC v2). Defensiv gegen eine v1-Antwort ohne die Spalte und
  // gegen einen 0-Wert trotz vorhandener Zeilen: weniger Produkte als
  // sichtbare Zeilen zu behaupten waere sicher falsch, also mindestens
  // `rows.length`. Leere Liste bleibt 0.
  const totalProducts =
    rows.length === 0 ? 0 : Math.max(toWireNumber(rows[0]?.total_products), rows.length);
  return { products, latestDate, rowCount, totalProducts };
}

const EMPTY_RESULT: AdsPerformanceResult = {
  products: [],
  latestDate: null,
  rowCount: 0,
  totalProducts: 0,
};

/** UTC-Datum `heute - days` als `YYYY-MM-DD`. Die Tabelle fuehrt `date` als
 *  Kalendertag des Ads-Kontos; eine lokale Zeitzone haette hier nichts zu suchen. */
function windowStart(days: number): string {
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - days);
  return from.toISOString().slice(0, 10);
}

/**
 * Ads-Leistung eines Feeds im gewaehlten Fenster, verdichtet je Produkt.
 *
 * Die Verdichtung laeuft SERVER-seitig in der RPC `ads_performance_summary`
 * (Migration 20260816120000), nicht mehr im Browser. Der Grund ist Issue #180:
 * der alte Pfad holte alle Roh-Tageszeilen in 1000er-Seiten sequenziell --
 * beim 30-Tage-Fenster auf einem 15k-Produkt-Feed rund 210k Zeilen in 210+
 * Requests hintereinander, Ladezeit im Minutenbereich. Die RPC liefert EINE
 * Zeile je Produkt, der Client uebersetzt nur noch (`mapSummaryRows`).
 *
 * ⚠ `ads_account_id` IST TEIL DER ABFRAGE UND MUSS ES BLEIBEN -- er lebt
 * jetzt als Pflicht-Parameter `p_ads_account_id` der RPC. Der Unique-Key der
 * Tabelle ist `(ads_account_id, product_id, date)` (Spec D5), er laesst also
 * MEHRERE Zeilen fuer dasselbe `(feed_id, product_id, date)` zu, sofern sie
 * verschiedene Ads-Konten tragen. Genau das ist erreichbar:
 * `set_active_google_ads_account` deaktiviert nur Geschwister DESSELBEN
 * `google_account_id`. Ohne diesen Filter wuerden Kosten und Conversions
 * doppelt gezaehlt, und zwar lautlos.
 */
export function useAdsProductPerformance(
  feedId: string | null,
  adsAccountId: string | null,
  windowDays: number,
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['ads-product-performance', feedId, user?.id ?? null, adsAccountId, windowDays],
    enabled: !!feedId && !!user?.id && !!adsAccountId,
    staleTime: 120_000,
    queryFn: async (): Promise<AdsPerformanceResult> => {
      if (!feedId || !adsAccountId) return EMPTY_RESULT;

      const { data, error } = await supabase
        .rpc('ads_performance_summary', {
          p_feed_id: feedId,
          p_ads_account_id: adsAccountId,
          p_from: windowStart(windowDays),
        });
      if (error) throw error;
      return mapSummaryRows(data ?? []);
    },
  });
}

// ── Hook: aktivieren und trennen ────────────────────────────────────────────

/**
 * Die beiden einzigen Schreibpfade, die das Panel hat.
 *
 * Beide laufen ueber SECURITY-DEFINER-RPCs mit Besitzpruefung im Rumpf, NICHT
 * ueber ein `UPDATE`-Grant: die Tabelle traegt OAuth-Credentials, und ein
 * Schreibrecht, das nur ein aus `USING` geerbtes `WITH CHECK` bewacht, ist
 * exakt der Fehler von `merchant_registrations` (SCHEMA.md, "Eine Policy
 * schuetzt nur die Spalten, die sie nennt").
 *
 * Beide RPCs geben `false` zurueck, wenn die Zeile nicht existiert, dem
 * Aufrufer nicht gehoert oder (beim Aktivieren) ein Verwaltungskonto ist --
 * bewusst ununterscheidbar, sonst waere es ein Existenz-Orakel auf fremde IDs.
 * Ein `false` ist hier deshalb ein Fehler und kein stiller Nicht-Erfolg.
 */
export function useAdsAccountActions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ads-accounts', user?.id ?? null] });
    queryClient.invalidateQueries({ queryKey: ['ads-product-performance'] });
    // Teaser-Kontozeile (useLinkedAdsAccount, staleTime 30 min) soll nach
    // Connect/Disconnect sofort stimmen, nicht erst in einer halben Stunde
    // (Review-Ledger-Minor). Praefix ueber alle MCs, bewusst ohne mcRowId.
    queryClient.invalidateQueries({ queryKey: [LINKED_ADS_ACCOUNT_QUERY_KEY] });
  };

  const activateMutation = useMutation({
    mutationFn: async (adsAccountId: string) => {
      const { data, error } = await supabase.rpc('set_active_google_ads_account', {
        p_ads_account_id: adsAccountId,
      });
      if (error) throw error;
      if (data === false) throw new Error('ADS_ACCOUNT_ACTIVATION_REFUSED');
      return true;
    },
    onSettled: invalidate,
  });

  const disconnectMutation = useMutation({
    mutationFn: async (adsAccountId: string) => {
      const { data, error } = await supabase.rpc('disconnect_google_ads_account', {
        p_ads_account_id: adsAccountId,
      });
      if (error) throw error;
      if (data === false) throw new Error('ADS_ACCOUNT_DISCONNECT_REFUSED');
      return true;
    },
    onSettled: invalidate,
  });

  return {
    activateAccount: activateMutation.mutateAsync,
    isActivating: activateMutation.isPending,
    disconnectAccount: disconnectMutation.mutateAsync,
    isDisconnecting: disconnectMutation.isPending,
    /**
     * Fuer den Connect-Rueckweg der Seite: das Verbinden laeuft nicht ueber
     * eine Mutation hier, sondern ueber das Popup von `ConnectGoogleAdsButton`.
     * Dieselbe Funktion an `onConnected` haengen, dann sind Kontenliste,
     * Leistungszahlen und Teaser-Kontozeile nach dem Verbinden gleich stale.
     */
    invalidateAdsQueries: invalidate,
  };
}

export const ADS_ROLLOUT_IDS_QUERY_KEY = 'ads-rollout-account-ids';

/**
 * IDs der eigenen `google_ads_accounts`-Zeilen, deren Kundennummer im
 * Ads-Rollout steht (RPC `ads_rollout_account_ids`, WP0/A8G-01). Ein Fehler
 * bleibt ein Fehler und wird NIE zu einer leeren Menge: die Leser sperren bei
 * unbekanntem Ergebnis (fail-closed).
 */
export function useAdsRolloutAccountIds() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [ADS_ROLLOUT_IDS_QUERY_KEY, user?.id ?? null],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ReadonlySet<string>> => {
      const { data, error } = await supabase.rpc('ads_rollout_account_ids');
      if (error) throw error;
      if (!Array.isArray(data)) throw new Error('ADS_ROLLOUT_IDS_MALFORMED');
      return new Set(data.map((row: { ads_account_id: string }) => row.ads_account_id));
    },
  });
}
